import { useForm } from 'react-hook-form'
import { UpdatePlaylistInput } from '@frontend-mono/shared/data-access-graphql'
import { useTranslation } from 'next-i18next'
import { UiModalContent } from '@frontend-mono/main/playlist/ui-modal-content'
import {
  useCheckIsDuplicatedTitle,
  useCreatePlaylist,
  useGetPlaylists,
  useUpdatePlaylist,
} from '@frontend-mono/main/playlist/data-access-playlist'
import { TitleInput } from '@frontend-mono/main/playlist/ui-title-input'
import { NewPlaylistButton } from '@frontend-mono/main/playlist/ui-modal-new-button'
import { openModal } from '@frontend-mono/shared/modal/util-open-modal'

type ModalContentProps = {
  topicIdList: string[]
  afterUpdateCallback: (targetId: string) => void
}

export const ModalContent = ({ topicIdList, afterUpdateCallback }: ModalContentProps) => {
  const { t } = useTranslation()
  const { playlists, hasAlready } = useGetPlaylists()
  const [createPlaylist] = useCreatePlaylist()
  const { updatePlaylist } = useUpdatePlaylist()
  const { checkIsDuplicated } = useCheckIsDuplicatedTitle()

  const handleUpdatePlaylist = async (input: UpdatePlaylistInput) => {
    await updatePlaylist({ playlist: input })
    afterUpdateCallback(input.id)
  }

  const handleAdd = async (playlistId: string) => {
    const targetPlaylist = playlists.find((item) => item.id === playlistId)
    const input = {
      id: playlistId,
      topicIds: [...(targetPlaylist?.topicIds ?? []), ...topicIdList],
      pinned: targetPlaylist?.pinned,
    }

    if (!hasAlready(playlistId, topicIdList)) {
      await handleUpdatePlaylist(input)
    } else {
      openModal({
        title: t('Topic_already_title'),
        icon: 'alert',
        content: t('Topic_already_description'),
        primaryButton: {
          text: t('Topic_already_confirm'),
          onClick: async (close) => {
            await handleUpdatePlaylist(input)
            close()
          },
        },
        secondaryButton: {
          text: t('Topic_already_cancel'),
        },
      })
    }
  }

  const { control, resetField, handleSubmit } = useForm<{ title: string }>({
    mode: 'onChange',
  })

  const handleCreate = async () => {
    let success = false
    await handleSubmit(({ title }) => {
      success = true
      resetField('title')
      createPlaylist({
        variables: {
          input: {
            title,
            topicIds: [],
          },
        },
      })
    })()
    return success
  }

  return (
    <UiModalContent
      list={playlists}
      ModalNewButton={(
        <NewPlaylistButton
          TitleInput={<TitleInput control={control} checkIsDuplicated={checkIsDuplicated} />}
          onCreate={handleCreate}
          onCancel={() => {
            resetField('title')
          }}
        />
      )}
      onAdd={handleAdd}
    />
  )
}
